import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { pushGlobalConfirmationModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { deinitialize } from '../redux/actions';
import { UseFormMethods } from 'react-hook-form';
import { SubmitFormValues, FormStatusType } from '../redux/types';
import * as React from 'react';

export function useSafeClose(
  isEdit: boolean,
  isDraft: boolean,
  formContext: UseFormMethods<SubmitFormValues>,
  formStatus: FormStatusType,
  requestClose: (event?: React.MouseEvent | React.KeyboardEvent) => void
) {
  const dispatch = useDispatch();
  const {
    formState: { isDirty, dirtyFields },
  } = formContext;

  // Ignore the attachments field dirty value because it won't affect the submit data
  const isDirtyForm = useMemo(
    () =>
      isDirty &&
      (Object.keys(dirtyFields).length !== 1 || !dirtyFields['attachments']),
    [isDirty, dirtyFields]
  );

  const requestDeinitializeAndClose = useCallback(
    (event?: React.MouseEvent | React.KeyboardEvent) => {
      dispatch(deinitialize());
      requestClose(event);
    },
    [dispatch, requestClose]
  );

  // If there's unsaved data or if it's a new upload, show the confirmation modal before close
  const requestSafeClose = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent) => {
      if (isDirtyForm) {
        dispatch(
          pushGlobalConfirmationModal({
            titleTransKey:
              isEdit || isDraft
                ? 'deviationSubmit.unsavedChanges.edit.title'
                : 'deviationSubmit.unsavedChanges.title',
            textTransKey:
              isEdit || isDraft
                ? 'deviationSubmit.unsavedChanges.edit.text'
                : 'deviationSubmit.unsavedChanges.text',
            cancelBtnLabelTransKey:
              'deviationSubmit.unsavedChanges.button.cancel',
            confirmBtnLabelTransKey:
              'deviationSubmit.unsavedChanges.button.confirm',
            confirmBtnType: 'danger',
            onConfirm: requestDeinitializeAndClose,
          })
        );
      } else {
        requestDeinitializeAndClose(event);
      }
    },
    [isDirtyForm, isEdit, isDraft, requestDeinitializeAndClose, dispatch]
  );

  useEffect(() => {
    if (['idle', 'error'].includes(formStatus) && window) {
      const beforeUnload = (e: BeforeUnloadEvent) => {
        if (isDirtyForm) {
          e.preventDefault();
          e.returnValue = '';
        }
      };
      window.addEventListener('beforeunload', beforeUnload);
      return () => {
        window.removeEventListener('beforeunload', beforeUnload);
      };
    }
  }, [isDirtyForm, formStatus]);

  return { requestDeinitializeAndClose, requestSafeClose };
}
